import type { EmailCompositionValue } from '@kula-ai/components';

/**
 * Shared primitives for the kula `Email` composers.
 *
 * Every surface used to carry its own copy of these, which is how the
 * `isSameEmailValue` guards drifted apart — several compared only the fields
 * their surface happened to show, so enabling another field would have made
 * the guard swallow real edits.
 */

export interface EmailUser {
  id?: string | number;
  email?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  avatar?: string | null;
  isSharedEmail?: boolean;
  [key: string]: unknown;
}

/** Surfaces key their from/cc/bcc values either by user id or by email. */
export type EmailValueKey = 'id' | 'email';

export const displayName = (user: EmailUser): string =>
  user.name?.trim() ||
  `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
  user.email ||
  '';

export const toIdString = (id: string | number | null | undefined): string =>
  id === null || id === undefined ? '' : String(id);

/**
 * Drops blank entries. A template row can carry empty cc/bcc slots, and
 * `Email` expands those rows whenever the list is non-empty — so a `[null]`
 * would open both rows showing nothing selected.
 */
export const toAddressList = (list: unknown): string[] =>
  Array.isArray(list)
    ? list
        .map((item) =>
          typeof item === 'number' ? String(item) : (item as string)
        )
        .filter((item): item is string => typeof item === 'string' && !!item)
    : [];

/** kula's `to` is `string | string[]`; normalise for comparison and payloads. */
export const asList = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];

export const sameList = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && a.every((item, i) => item === b[i]);

/**
 * Guards against `Email`'s re-emits being stored as new state, which loops.
 *
 * Compares every field rather than the subset a given surface renders: a
 * partial comparison silently drops edits the moment that surface turns
 * another field on.
 */
export const isSameEmailValue = (
  a: EmailCompositionValue,
  b: EmailCompositionValue
): boolean =>
  a.from === b.from &&
  a.subject === b.subject &&
  a.content === b.content &&
  a.template === b.template &&
  a.ics === b.ics &&
  sameList(asList(a.to), asList(b.to)) &&
  sameList(a.cc, b.cc) &&
  sameList(a.bcc, b.bcc);

/** The option value a surface stores for a user, per its `valueKey`. */
export const optionValue = (
  user: EmailUser,
  valueKey: EmailValueKey
): string => (valueKey === 'id' ? toIdString(user.id) : (user.email ?? ''));

/**
 * Builds kula `Select` options. `id`-keyed surfaces resolve senders by id on
 * save; `email`-keyed ones match on the address.
 */
export const toSelectOptions = (
  users: EmailUser[] = [],
  valueKey: EmailValueKey = 'id'
) =>
  users
    .filter((user) => !!optionValue(user, valueKey))
    .map((user) => ({
      value: optionValue(user, valueKey),
      label: displayName(user) || optionValue(user, valueKey),
      description: valueKey === 'id' ? user.email : user.name && user.email,
      image: user.avatar ?? undefined,
    }));

/** Finds the option object behind a stored value — it carries `isSharedEmail`. */
export const resolveEmailUser = (
  value: string,
  users: EmailUser[] = [],
  valueKey: EmailValueKey = 'id'
): EmailUser | undefined =>
  users.find((user) => optionValue(user, valueKey) === value);
